import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Rocket, Shield, Sun, Moon, Zap,
  GitBranch, Server, Layers, Network, Globe, ShieldCheck,
  Key, Headphones, Monitor, TrendingDown, BarChart3, ChevronDown,
  ChevronRight, Radio, Database, Container, Building2, Cloud, ShieldAlert,
  Activity, FileBarChart, Bell, UserCog, Package, HardDrive,
} from 'lucide-react'
import { ApteanLogo, ApteanWordmark } from '@/components/common/ApteanLogo'
import { useThemeStore } from '@/stores/themeStore'
import { useUserRoles } from '@/contexts/UserRolesContext'
import { useAuthStore } from '@/stores/authStore'
import styles from './Sidebar.module.css'

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  badge?: string
  href?: string
}

interface SubGroup {
  id: string
  label: string
  items: NavItem[]
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const azureSubGroups: SubGroup[] = [
  {
    id: 'deployments',
    label: 'Deployments',
    items: [
      { to: '/one-click-deploy', icon: Zap,        label: 'OneClick App Deploy' },
      { to: '/deployments',     icon: Rocket,     label: 'All Deployments' },
      { to: '/deployments/new', icon: Rocket,     label: 'New Environment' },
      { to: '/app-in-aks',      icon: Layers,     label: 'App in AKS' },
      { to: '/mcp-deployment',  href: 'https://selfserve.apteancloud.com/mcp', icon: Server, label: 'MCP Servers' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { to: '/release-management', href: 'https://appcentral-int.aptean.com/release/', icon: GitBranch,   label: 'Release Management' },
      { to: '/patch-management',   icon: ShieldCheck, label: 'Patch Management' },
      { to: '/aks-management',     icon: Container,   label: 'AKS Management' },
    ],
  },
  {
    id: 'network',
    label: 'Network Config',
    items: [
      { to: '/haproxy',      icon: Network, label: 'HAProxy Config' },
      { to: '/cloudflare',   icon: Globe,   label: 'Cloudflare' },
      { to: '/certificates', icon: Key,     label: 'Certificates' },
    ],
  },
  {
    id: 'observability',
    label: 'Observability',
    items: [
      { to: '/status-central', icon: Radio,    label: 'Status Central', badge: 'Live' },
      { to: '/monitoring',     icon: Monitor,  label: 'Live Monitoring' },
      { to: '/cmdb', href: 'https://cmdb.aptean.com/dashboard', icon: Database, label: 'Aptean CMDB' },
    ],
  },
  {
    id: 'financial',
    label: 'Financial',
    items: [
      { to: '/cost-optimization', icon: TrendingDown, label: 'Cost Optimization' },
      { to: '/cost-reports',      icon: BarChart3,    label: 'Cost Reports' },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      { to: '/service-desk', href: 'https://servicedesk.aptean.com', icon: Headphones, label: 'Aptean Service Desk' },
    ],
  },
]

// ─── NavItem renderer ─────────────────────────────────────────────────────────

function NavItemEl({ item }: { item: NavItem }) {
  const cls = styles.navItem
  const accent = 'var(--aptean-teal)'

  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noreferrer"
        className={cls} style={{ '--accent': accent } as React.CSSProperties}>
        <item.icon size={14} />
        <span className={styles.navItemLabel}>{item.label}</span>
        {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
      </a>
    )
  }
  return (
    <NavLink to={item.to} end
      className={({ isActive }) => `${cls} ${isActive ? styles.navItemActive : ''}`}
      style={{ '--accent': accent } as React.CSSProperties}>
      <item.icon size={14} />
      <span className={styles.navItemLabel}>{item.label}</span>
      {item.badge && <span className={styles.navBadge}>{item.badge}</span>}
    </NavLink>
  )
}

// ─── Sub-group (Deployments / Operations / …) ─────────────────────────────────

function SubGroupSection({ group, defaultOpen }: { group: SubGroup; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen ?? group.id === 'deployments')

  return (
    <div className={styles.subGroup}>
      <button className={styles.subGroupHeader} onClick={() => setOpen(o => !o)}>
        {open
          ? <ChevronDown size={11} className={styles.subGroupChevron} />
          : <ChevronRight size={11} className={styles.subGroupChevron} />
        }
        <span>{group.label}</span>
      </button>
      {open && (
        <div className={styles.subGroupItems}>
          {group.items.map(item => <NavItemEl key={item.to} item={item} />)}
        </div>
      )}
    </div>
  )
}

// ─── Disaster Recovery sub-groups ─────────────────────────────────────────────

const drSubGroups: SubGroup[] = [
  {
    id: 'dr-overview',
    label: 'Overview',
    items: [
      { to: '/disaster-recovery',           icon: Package,      label: 'DR Inventory' },
      { to: '/disaster-recovery/rpo-rto',   icon: Activity,     label: 'RPO/RTO Tracking' },
    ],
  },
  {
    id: 'dr-drills',
    label: 'Drills',
    items: [
      { to: '/disaster-recovery/drills',             icon: ShieldCheck, label: 'DR Drills' },
      { to: '/disaster-recovery/drill-templates',    icon: Layers,      label: 'Drill Templates' },
    ],
  },
  {
    id: 'dr-ops',
    label: 'Operations',
    items: [
      { to: '/disaster-recovery/backup',         icon: HardDrive,    label: 'Backup Status' },
      { to: '/disaster-recovery/reports',        icon: FileBarChart, label: 'Reports' },
      { to: '/disaster-recovery/notifications',  icon: Bell,         label: 'Notifications' },
    ],
  },
  {
    id: 'dr-admin',
    label: 'Administration',
    items: [
      { to: '/disaster-recovery/admin', icon: UserCog, label: 'DR Admin & RBAC' },
    ],
  },
]

// ─── Disaster Recovery collapsible parent ─────────────────────────────────────

function DisasterRecoverySection() {
  const location = useLocation()
  const allItems = drSubGroups.flatMap(g => g.items)
  const hasActive = allItems.some(i => location.pathname === i.to || location.pathname.startsWith(i.to + '/'))
  const [open, setOpen] = useState(hasActive)

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={`${styles.topLevelItem} ${styles.topLevelBtn} ${hasActive ? styles.topLevelActive : ''}`}
      >
        <ShieldAlert size={16} />
        <span className={styles.navItemLabel}>Disaster Recovery</span>
        {open
          ? <ChevronDown size={13} className={styles.topLevelChevron} />
          : <ChevronRight size={13} className={styles.topLevelChevron} />
        }
      </button>
      {open && (
        <div className={styles.subGroups}>
          {drSubGroups.map(group => (
            <SubGroupSection key={group.id} group={group} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Azure Cloud collapsible parent ───────────────────────────────────────────

function AzureCloudSection() {
  const location = useLocation()
  const { isAdmin, isDeployer } = useUserRoles()

  let visibleGroups: SubGroup[]
  if (isAdmin || isDeployer) {
    visibleGroups = azureSubGroups
  } else {
    // Standard/team users: only the Deployments group, restricted to
    // New Environment + All Deployments — nothing else (no Operations,
    // Network, Observability, Financial, Support).
    const deploymentsGroup = azureSubGroups.find(g => g.id === 'deployments')
    visibleGroups = deploymentsGroup
      ? [{
          ...deploymentsGroup,
          items: deploymentsGroup.items.filter(
            i => i.to === '/deployments/new' || i.to === '/deployments'
          ),
        }]
      : []
  }

  const allItems = visibleGroups.flatMap(g => g.items)
  const hasActive = allItems.some(i => !i.href && location.pathname.startsWith(i.to))
  const [open, setOpen] = useState(true)

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className={`${styles.topLevelItem} ${styles.topLevelBtn} ${hasActive ? styles.topLevelActive : ''}`}
      >
        <Cloud size={16} />
        <span className={styles.navItemLabel}>Azure Cloud</span>
        {open
          ? <ChevronDown size={13} className={styles.topLevelChevron} />
          : <ChevronRight size={13} className={styles.topLevelChevron} />
        }
      </button>
      {open && (
        <div className={styles.subGroups}>
          {visibleGroups.map(group => (
            <SubGroupSection key={group.id} group={group} defaultOpen={group.id === 'deployments'} />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

export function Sidebar() {
  const { theme, toggle } = useThemeStore()
  const { isAdmin, isDeployer } = useUserRoles()
  const { user } = useAuthStore()
  const accent = { '--accent': 'var(--aptean-teal)' } as React.CSSProperties

  const displayName = user?.name || user?.username || 'Admin'
  const initials = displayName[0]?.toUpperCase() ?? 'A'

  return (
    <aside className={styles.sidebar}>
      {/* Brand */}
      <div className={styles.brand}>
        <ApteanLogo size={32} />
        <div>
          <div className={styles.brandName}>Platform Central</div>
          <div className={styles.brandSub}>Aptean Platform</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.sectionLabel}>HOME</div>

        {/* Platform Central */}
        <NavLink to="/dashboard"
          className={({ isActive }) => `${styles.topLevelItem} ${isActive ? styles.topLevelActive : ''}`}
          style={accent}>
          <LayoutDashboard size={16} />
          <span className={styles.navItemLabel}>Platform Central</span>
        </NavLink>

        {/* Azure Cloud (nested) */}
        <AzureCloudSection />

        {/* Disaster Recovery — admin/deployer only */}
        {(isAdmin || isDeployer) && <DisasterRecoverySection />}

        {/* IBM Cloud — admin/deployer only */}
        {(isAdmin || isDeployer) && (
          <a href="https://selfserve.apteancloud.com/ibm-cloud" target="_blank" rel="noreferrer"
            className={styles.topLevelItem} style={accent}>
            <Building2 size={16} />
            <span className={styles.navItemLabel}>IBM Cloud</span>
          </a>
        )}

        {/* Admin Settings — admin + deployer */}
        {(isAdmin || isDeployer) && (
          <NavLink to="/admin"
            className={({ isActive }) => `${styles.topLevelItem} ${isActive ? styles.topLevelActive : ''}`}
            style={accent}>
            <Shield size={16} />
            <span className={styles.navItemLabel}>Admin Settings</span>
          </NavLink>
        )}

        {/* Teams & Access (includes User Roles tab) — admin only */}
        {isAdmin && (
          <NavLink to="/teams"
            className={({ isActive }) => `${styles.topLevelItem} ${isActive ? styles.topLevelActive : ''}`}
            style={accent}>
            <UserCog size={16} />
            <span className={styles.navItemLabel}>Teams & Access</span>
          </NavLink>
        )}
      </nav>

      {/* Footer */}
      <div className={styles.sidebarFooter}>
        <div className={styles.footerUserRow}>
          <div className={styles.footerUser}>
            <div className={styles.avatar}>{initials}</div>
            <div className={styles.footerUserInfo}>
              <div className={styles.footerUserName}>{displayName}</div>
              <div className={styles.footerUserRole}>{isAdmin ? 'Super Admin' : isDeployer ? 'Admin' : 'Member'}</div>
            </div>
          </div>
          <button className={styles.themeToggle} onClick={toggle}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}>
            {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
          </button>
        </div>
        <div className={styles.apteanWordmark}>
          <ApteanWordmark />
          <div className={styles.poweredBy}>
            <span className={styles.poweredByLabel}>POWERED BY</span>
            <div className={styles.poweredByBadge}>
              <Layers size={13} className={styles.poweredByIcon} />
              <span>Platform Engineering</span>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
